import { useEffect, useMemo, useRef, useState } from "react";
import type { Player } from "../domain/types";
import { pickSettle, tap } from "../lib/haptic";

interface Props {
  players: Player[];
  /** The player id we will settle on. */
  finalId: string;
  onSettled: () => void;
  /** Total animation duration in ms. */
  duration?: number;
}

const ROW_HEIGHT = 96;
const VISIBLE_ROWS = 5;

/**
 * Slot-machine name reveal — a vertical wheel of player names that decelerates
 * and settles on `finalId` at the centre.
 */
export function PickingAnimation({ players, finalId, onSettled, duration = 1900 }: Props) {
  const strip = useMemo(() => buildStrip(players, finalId), [players, finalId]);
  const finalIndex = strip.length - 1;
  const finalOffset = finalIndex * ROW_HEIGHT;

  const [offset, setOffset] = useState(0);
  const settledRef = useRef(false);
  const startRef = useRef<number>(0);

  useEffect(() => {
    settledRef.current = false;
    startRef.current = performance.now();
    let raf = 0;
    // Track which "soft tick" we have already fired during decel so the host
    // feels rhythm: tick … tick … SETTLE without watching the wheel.
    let firedTicks = 0;
    function frame(now: number) {
      const t = now - startRef.current;
      const k = Math.min(1, t / duration);
      const eased = 1 - Math.pow(1 - k, 3);
      setOffset(eased * finalOffset);
      if (firedTicks < 1 && k >= 0.7) {
        firedTicks = 1;
        tap(10);
      } else if (firedTicks < 2 && k >= 0.88) {
        firedTicks = 2;
        tap(14);
      }
      if (k >= 1) {
        if (!settledRef.current) {
          settledRef.current = true;
          pickSettle();
          onSettled();
        }
        return;
      }
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [finalOffset, duration, onSettled]);

  const halfVisible = (VISIBLE_ROWS - 1) / 2;
  const viewportHeight = ROW_HEIGHT * VISIBLE_ROWS;

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 relative">
      <div className="text-xs uppercase tracking-[0.3em] opacity-60 mb-3">
        ⋯
      </div>
      <div
        className="relative w-full max-w-sm"
        style={{
          height: viewportHeight,
          maskImage:
            "linear-gradient(180deg,transparent 0%,#000 30%,#000 70%,transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(180deg,transparent 0%,#000 30%,#000 70%,transparent 100%)",
        }}
      >
        <div
          className="absolute left-0 right-0 top-0"
          style={{
            transform: `translateY(${halfVisible * ROW_HEIGHT - offset}px)`,
            willChange: "transform",
          }}
        >
          {strip.map((p, i) => {
            // Centre-proximity emphasis: the name in the ring is bigger and
            // glows, neighbours shrink and dim — the wheel reads as a physical
            // drum, not a flat list. d = rows away from the ring centre.
            const d = Math.abs(i * ROW_HEIGHT - offset) / ROW_HEIGHT;
            const scale = Math.max(0.8, 1.12 - 0.16 * d);
            const opacity = Math.max(0.3, 1 - 0.32 * d);
            return (
              <div
                key={i}
                className="flex items-center justify-center font-display font-black tracking-tight"
                style={{
                  height: ROW_HEIGHT,
                  fontSize: "2.5rem",
                  lineHeight: 1,
                  transform: `scale(${scale})`,
                  opacity,
                  textShadow:
                    d < 0.5 ? "0 0 24px var(--accent, rgba(255,255,255,0.6))" : "none",
                }}
              >
                {p.name}
              </div>
            );
          })}
        </div>
        <div
          className="pointer-events-none absolute left-0 right-0"
          style={{
            top: `calc(50% - ${ROW_HEIGHT / 2}px)`,
            height: ROW_HEIGHT,
            borderRadius: 18,
            border: "1.5px solid var(--accent, rgba(255,255,255,0.6))",
            boxShadow:
              "0 0 24px 4px var(--accent, rgba(255,255,255,0.4)), inset 0 0 24px 2px var(--accent, rgba(255,255,255,0.18))",
            background:
              "linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)",
          }}
        />
        <div
          className="pointer-events-none absolute left-2"
          style={{ top: `calc(50% - 8px)`, color: "var(--accent,#fff)" }}
        >
          ◀
        </div>
        <div
          className="pointer-events-none absolute right-2"
          style={{ top: `calc(50% - 8px)`, color: "var(--accent,#fff)" }}
        >
          ▶
        </div>
      </div>
    </div>
  );
}

function buildStrip(players: Player[], finalId: string): Player[] {
  if (players.length === 0) return [];
  const finalPlayer = players.find((p) => p.id === finalId) ?? players[0]!;
  const rotations = 4;
  const filler: Player[] = [];
  for (let r = 0; r < rotations; r++) {
    const start = r % players.length;
    for (let i = 0; i < players.length; i++) {
      filler.push(players[(start + i) % players.length]!);
    }
  }
  filler.push(finalPlayer);
  return filler;
}
